/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, getter-return */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import PageType from '@linc-technologies/linced-common/enums/page-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { tagName } from '@ember-decorators/component';

@tagName('')
export default class PlaceholderDialog extends Component {
  @action
  setSelectedPlaceholderField(value) {
    this.selectedPlaceholderField = value;
  }

  // Props
  origin = null;

  parent = null;
  editor = null;
  @tracked pageType = PageType.STUDENT;
  fieldGroups = null;
  fieldTemplateSets = null;
  @tracked
  placeholderDialogOpen = false;
  onClose() {}

  // State
  get placeholderTypes() {
    let placeholderTypes = [{ value: 'basic', label: 'Basic placeholder' }];
    switch (this.pageType) {
      case PageType.STUDENT:
        placeholderTypes.push(
          {
            value: 'field',
            personType: PersonType.LEARNER,
            label: 'Value of a student field',
          },
          {
            value: 'field',
            personType: PersonType.CAREGIVER,
            label: 'Value of a caregiver field',
          },
        );
        break;

      case PageType.STAFF:
        placeholderTypes.push({
          value: 'field',
          personType: PersonType.STAFF,
          label: 'Value of a staff field',
        });
        break;
    }
    return placeholderTypes;
  }

  @tracked selectedPlaceholderType = null;

  @computed('fieldTemplateSets.[]', 'selectedPlaceholderType')
  get filteredTemplateSets() {
    if (isEmpty(this.fieldTemplateSets)) {
      return [];
    }
    let personType = this.get('selectedPlaceholderType.personType');
    return this.fieldTemplateSets
      .filter((set) => set.personType === personType)
      .sort(
        (a, b) =>
          a.sequence - b.sequence || (a.name || '').localeCompare(b.name || ''),
      );
  }

  @tracked selectedTemplateSet = null;

  @computed(
    'selectedPlaceholderType',
    'pageType',
    'fieldGroups.[]',
    'fieldTemplateSets.[]',
    'selectedTemplateSet',
  )
  get placeholderFields() {
    let { value: placeholderType, personType } = this.selectedPlaceholderType;
    switch (placeholderType) {
      case 'field':
        if (isEmpty(personType) || isEmpty(this.fieldGroups)) {
          // No fields to display.
          return [];
        }

        if (!isEmpty(this.fieldTemplateSets)) {
          // If we have Template Sets, this must be a template (admin app).
          if (isEmpty(this.selectedTemplateSet)) {
            // Wait until a Template Set is selected before returning fields.
            return [];
          }

          // Only return fields and groups which are included in the selected
          // Template Set.
          return this.selectedTemplateSet.groups
            .slice()
            .sort(
              (a, b) =>
                a.sequence - b.sequence ||
                (a.name || '').localeCompare(b.name || ''),
            )
            .map((group) => {
              return {
                groupName: group.name,
                options: group.fields
                  .filter(
                    (field) =>
                      field.get('templateSetId.id') ===
                      this.get('selectedTemplateSet.id'),
                  )
                  .sort(
                    (a, b) =>
                      a.sequence - b.sequence ||
                      (a.label || '').localeCompare(b.label || ''),
                  ),
              };
            })
            .filter((group) => group.options.length);
        }

        return this.fieldGroups
          .filter((group) => group.personType === personType)
          .sort(
            (a, b) =>
              a.sequence - b.sequence ||
              (a.name || '').localeCompare(b.name || ''),
          )
          .map((group) => {
            return {
              groupName: group.name,
              options: group.fields
                .slice()
                .sort(
                  (a, b) =>
                    a.sequence - b.sequence ||
                    (a.label || '').localeCompare(b.label || ''),
                ),
            };
          })
          .filter((group) => group.options.length);

      case 'basic':
        return [
          // Date/time placeholders.
          { key: 'date_short', label: 'Date (short)' },
          { key: 'date_long', label: 'Date (long)' },
          { key: 'time_12hr', label: 'Time (12 hour)' },
          { key: 'time_24hr', label: 'Time (24 hour)' },

          // School details.
          { key: 'school_name', label: 'School name' },
          { key: 'school_phone', label: 'School phone number' },
          { key: 'school_email', label: 'School email address' },

          // School's physical address.
          {
            key: 'physical_number',
            label: 'School address (physical): street number',
          },
          {
            key: 'physical_street',
            label: 'School address (physical): street name',
          },
          {
            key: 'physical_locality',
            label: 'School address (physical): suburb',
          },
          {
            key: 'physical_district',
            label: 'School address (physical): city',
          },
          {
            key: 'physical_postcode',
            label: 'School address (physical): postcode',
          },

          // School's postal address.
          {
            key: 'postal_number',
            label: 'School address (postal): street number',
          },
          {
            key: 'postal_street',
            label: 'School address (postal): street name',
          },
          {
            key: 'postal_locality',
            label: 'School address (postal): suburb',
          },
          { key: 'postal_district', label: 'School address (postal): city' },
          {
            key: 'postal_postcode',
            label: 'School address (postal): postcode',
          },
        ];
    }
  }

  @tracked
  selectedPlaceholderField = null;

  // Lifecycle Hooks
  init() {
    super.init(...arguments);
    this.selectedPlaceholderType = this.placeholderTypes[0];
  }

  @action
  closeDialog() {
    this.onClose();
  }

  @action
  setPlaceholderType(newType) {
    this.selectedPlaceholderType = newType;
    this.selectedPlaceholderField = null;
  }

  @action
  setTemplateSet(templateSet) {
    this.selectedTemplateSet = templateSet;
    this.selectedPlaceholderField = null;
  }

  @action
  insertPlaceholder() {
    if (isEmpty(this.editor)) {
      return;
    }
    let { value: placeholderType, personType } = this.selectedPlaceholderType;
    let icon = `<span class="material-icons">label_outline</span>`;
    let label = this.selectedPlaceholderField.label.trim();
    let personTypeAttr = '';
    if (!isEmpty(personType)) {
      personTypeAttr = `data-placeholder-person-type="${personType}"`;
      if (personType === PersonType.CAREGIVER) {
        label = `${PersonType.getLabel(personType)}: ${label}`.trim();
      }
    }
    this.editor.insertContent(
      `<span
        class="hero-template-placeholder"
        contenteditable="false"
        data-placeholder-type="${placeholderType}"
        ${personTypeAttr}
        data-placeholder-for="${this.selectedPlaceholderField.key}"
      >${icon}${label}</span>`,
    );
    this.placeholderDialogOpen = false;
    this.selectedPlaceholderField = null;
  }
}
